import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/services/api';
import { hasCapability } from '@/lib/capabilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ChevronLeft, RefreshCw, Bike, PackageCheck, Truck, AlertTriangle, LifeBuoy, Clock, User } from 'lucide-react';

const REASONS = ['CUSTOMER_UNAVAILABLE', 'WRONG_ADDRESS', 'CUSTOMER_REFUSED', 'RIDER_CANCELLED', 'DAMAGED', 'OTHER'];
const OVERRIDE_STATUSES = ['READY_DELIVERY', 'RIDER_ASSIGNED', 'OUT_FOR_DELIVERY', 'DELIVERED', 'CANCELLED'];

const Metric = ({ label, value, testid }) => (
  <div className="bg-white rounded-xl border border-slate-200 px-4 py-3 text-center min-w-[110px]">
    <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
    <p className="text-xl font-bold text-slate-900" data-testid={testid}>{value}</p>
  </div>
);

export default function DeliveryOps() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const storeId = user?.store_id;
  const canOperate = hasCapability(user, 'orders.operate');

  const [board, setBoard] = useState({ queue: [], riders: [], delivered_today: 0 });
  const [perf, setPerf] = useState(null);
  const [assignSel, setAssignSel] = useState({});          // orderId -> riderId
  const [excFor, setExcFor] = useState(null);              // order for exception dialog
  const [excReason, setExcReason] = useState('');
  const [excNote, setExcNote] = useState('');
  const [recFor, setRecFor] = useState(null);              // order for recovery dialog
  const [recStatus, setRecStatus] = useState('');
  const [recRider, setRecRider] = useState('');
  const [recReason, setRecReason] = useState('');

  const load = useCallback(async () => {
    try {
      const [b, p] = await Promise.all([
        apiClient.get(`/delivery/board/${storeId}`, { params: { _: Date.now() } }),
        apiClient.get(`/delivery/performance/${storeId}?days=7`, { params: { _: Date.now() } }),
      ]);
      setBoard(b.data); setPerf(p.data);
    } catch { toast.error('Failed to load delivery board'); }
  }, [storeId]);

  useEffect(() => {
    if (!user?.id) { navigate('/login'); return; }
    if (!canOperate) { toast.error('No permission'); navigate('/pos-dashboard'); return; }
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [load]);

  const riderName = (id) => board.riders.find((r) => r.id === id)?.name || '';

  const assign = async (order) => {
    const rid = assignSel[order.id];
    if (!rid) { toast.error('Select a rider'); return; }
    try {
      await apiClient.put(`/orders/${order.id}/manual-rider`, { rider_id: rid, rider_name: riderName(rid) });
      toast.success(`Rider ${riderName(rid)} assigned`); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Assign failed'); }
  };

  const confirmPickup = async (order) => {
    try { await apiClient.put(`/delivery/${order.id}/confirm-pickup`, {}); toast.success('Out for delivery'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const complete = async (order) => {
    try {
      const r = await apiClient.put(`/delivery/${order.id}/complete`, {});
      toast.success('Delivered'); if (r.data.warning) toast.warning(r.data.warning); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const submitException = async () => {
    if (!excReason) { toast.error('Choose a reason'); return; }
    try {
      const r = await apiClient.post(`/delivery/${excFor.id}/exception`, { reason_code: excReason, note: excNote || null });
      toast.success(r.data.reverted ? 'Exception logged — returned to dispatch queue' : 'Exception logged');
      setExcFor(null); setExcReason(''); setExcNote(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const submitRecovery = async () => {
    try {
      if (recRider !== '') {
        await apiClient.put(`/orders/${recFor.id}/manual-rider`, recRider ? { rider_id: recRider, rider_name: riderName(recRider) } : {});
      }
      if (recStatus) {
        await apiClient.put(`/orders/${recFor.id}/override-status`, { status: recStatus, reason: recReason || 'manual recovery' });
      }
      toast.success('Recovery applied');
      setRecFor(null); setRecStatus(''); setRecRider(''); setRecReason(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Recovery failed'); }
  };

  const groups = {
    READY_DELIVERY: board.queue.filter((o) => o.status === 'READY_DELIVERY'),
    RIDER_ASSIGNED: board.queue.filter((o) => o.status === 'RIDER_ASSIGNED'),
    OUT_FOR_DELIVERY: board.queue.filter((o) => o.status === 'OUT_FOR_DELIVERY'),
  };
  const COLS = [
    { key: 'READY_DELIVERY', label: 'Ready to Dispatch', icon: PackageCheck },
    { key: 'RIDER_ASSIGNED', label: 'Assigned', icon: Bike },
    { key: 'OUT_FOR_DELIVERY', label: 'Out for Delivery', icon: Truck },
  ];

  const OrderCard = ({ o }) => (
    <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`do-order-${o.id}`}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-slate-800">#{o.order_number} · ₹{Number(o.total || 0).toFixed(2)}</p>
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${(o.payment_status || '').toUpperCase() === 'PAID' ? 'text-green-600 bg-green-50' : 'text-amber-600 bg-amber-50'}`}>{o.payment_status || 'PENDING'}</span>
      </div>
      <p className="text-xs text-slate-500 truncate">{o.customer_name} · {(typeof o.delivery_address === 'string' ? o.delivery_address : (o.delivery_address?.address_line || o.delivery_address?.full_address || o.delivery_address?.line1)) || '—'}</p>
      {o.rider_name && <p className="text-xs text-slate-600 flex items-center gap-1"><User className="w-3 h-3" />{o.rider_name}</p>}
      {o.delivery_exception && <p className="text-[11px] text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{o.delivery_exception.reason_code}</p>}

      {o.status === 'READY_DELIVERY' && (
        <div className="flex gap-1">
          <select value={assignSel[o.id] || ''} onChange={(e) => setAssignSel((s) => ({ ...s, [o.id]: e.target.value }))}
            className="flex-1 text-xs border border-slate-200 rounded-lg px-2 py-1.5" data-testid={`do-rider-select-${o.id}`}>
            <option value="">Select rider…</option>
            {board.riders.map((r) => <option key={r.id} value={r.id}>{r.name}{r.online ? ' ●' : ''}</option>)}
          </select>
          <Button size="sm" className="bg-sspice hover:bg-sspice-hover" onClick={() => assign(o)} data-testid={`do-assign-${o.id}`}>Assign</Button>
        </div>
      )}
      {o.status === 'RIDER_ASSIGNED' && (
        <Button size="sm" className="w-full bg-sspice hover:bg-sspice-hover" onClick={() => confirmPickup(o)} data-testid={`do-pickup-${o.id}`}>Confirm Pickup</Button>
      )}
      {o.status === 'OUT_FOR_DELIVERY' && (
        <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={() => complete(o)} data-testid={`do-complete-${o.id}`}>Complete Delivery</Button>
      )}
      <div className="flex gap-1">
        <Button size="sm" variant="outline" className="flex-1 text-amber-600" onClick={() => setExcFor(o)} data-testid={`do-exception-${o.id}`}><AlertTriangle className="w-3.5 h-3.5 mr-1" />Exception</Button>
        <Button size="sm" variant="outline" className="flex-1 text-slate-600" onClick={() => setRecFor(o)} data-testid={`do-recover-${o.id}`}><LifeBuoy className="w-3.5 h-3.5 mr-1" />Recover</Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="delivery-ops-page">
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/pos-dashboard')} data-testid="do-back"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-sspice" />
            <div>
              <h1 className="font-heading text-lg font-bold text-slate-900">Delivery Operations</h1>
              <p className="text-xs text-slate-500">Assign riders, track stages & recover stuck deliveries</p>
            </div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="do-refresh"><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {/* Performance strip */}
      {perf && (
        <div className="max-w-6xl mx-auto px-4 pt-4 flex flex-wrap gap-3" data-testid="do-performance">
          <Metric label="Delivered (7d)" value={perf.total_delivered} testid="do-perf-delivered" />
          <Metric label="Today" value={board.delivered_today} testid="do-perf-today" />
          <Metric label="Avg Total (min)" value={perf.avg_total_min ?? '—'} testid="do-perf-avg" />
          <Metric label="On-time" value={perf.on_time_rate != null ? `${perf.on_time_rate}%` : '—'} testid="do-perf-ontime" />
          <Metric label="Exceptions (7d)" value={perf.exceptions_total} testid="do-perf-exceptions" />
        </div>
      )}

      <div className="max-w-6xl mx-auto p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        {COLS.map(({ key, label, icon: Icon }) => (
          <div key={key} className="space-y-3" data-testid={`do-col-${key}`}>
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <Icon className="w-4 h-4 text-sspice" /> {label}
              <span className="text-xs bg-slate-200 text-slate-600 rounded-full px-2">{groups[key].length}</span>
            </div>
            {groups[key].length === 0
              ? <p className="text-xs text-slate-400 py-4 text-center" data-testid={`do-empty-${key}`}>No orders</p>
              : groups[key].map((o) => <OrderCard key={o.id} o={o} />)}
          </div>
        ))}
      </div>

      {/* Exception dialog */}
      <Dialog open={!!excFor} onOpenChange={(o) => { if (!o) setExcFor(null); }}>
        <DialogContent className="max-w-sm" data-testid="do-exception-dialog">
          <DialogHeader>
            <DialogTitle>Report Delivery Exception</DialogTitle>
            <DialogDescription className="sr-only">Record a delivery exception with a reason.</DialogDescription>
          </DialogHeader>
          <p className="text-xs text-slate-500">Order #{excFor?.order_number}</p>
          <div className="grid grid-cols-2 gap-2">
            {REASONS.map((r) => (
              <button key={r} onClick={() => setExcReason(r)} data-testid={`do-exc-reason-${r}`}
                className={`text-xs py-2 rounded-lg border ${excReason === r ? 'bg-sspice text-white border-sspice' : 'border-slate-200 text-slate-600'}`}>{r.replace(/_/g, ' ')}</button>
            ))}
          </div>
          <Input placeholder="Note (optional)" value={excNote} onChange={(e) => setExcNote(e.target.value)} data-testid="do-exc-note" />
          <Button className="w-full bg-sspice hover:bg-sspice-hover" onClick={submitException} data-testid="do-exc-submit">Log Exception</Button>
        </DialogContent>
      </Dialog>

      {/* Recovery (fail-safe) dialog */}
      <Dialog open={!!recFor} onOpenChange={(o) => { if (!o) setRecFor(null); }}>
        <DialogContent className="max-w-sm" data-testid="do-recovery-dialog">
          <DialogHeader>
            <DialogTitle>Manual Recovery</DialogTitle>
            <DialogDescription className="sr-only">Reassign a rider or override the delivery status.</DialogDescription>
          </DialogHeader>
          <p className="text-xs text-slate-500">Order #{recFor?.order_number} · current: {recFor?.status}</p>
          <label className="text-xs font-semibold text-slate-600">Reassign / remove rider</label>
          <select value={recRider} onChange={(e) => setRecRider(e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-2 py-2" data-testid="do-rec-rider">
            <option value="">— no change —</option>
            <option value="__remove__" disabled>—</option>
            {board.riders.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <label className="text-xs font-semibold text-slate-600">Override status (bypass state machine)</label>
          <select value={recStatus} onChange={(e) => setRecStatus(e.target.value)} className="w-full text-sm border border-slate-200 rounded-lg px-2 py-2" data-testid="do-rec-status">
            <option value="">— no change —</option>
            {OVERRIDE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <Input placeholder="Reason (for audit)" value={recReason} onChange={(e) => setRecReason(e.target.value)} data-testid="do-rec-reason" />
          <Button className="w-full bg-sspice hover:bg-sspice-hover" onClick={submitRecovery} data-testid="do-rec-submit">Apply Recovery</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
